(async () => {
  if (!customElements.get('timeline-slider')) {
    const { Swiper } = await import('swiper');
    const { A11y, Autoplay, Navigation, Pagination, EffectFade } = await import('swiper/modules');

    class TimelineSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = this.querySelector('[data-swiper]');

        // Redefine self to be able to call it in swiper instance
        const self = this;

        // Stop when the swiper is not found
        if (!this.swiper) return;

        // Get section id
        this.sectionId = this.swiper.getAttribute('data-section-id') || '';

        // Default to improve user experience
        this.swiperOptions = {
          modules: [A11y, Autoplay, Navigation, Pagination, EffectFade],
          threshold: 10,
          pagination: {
            el: `.swiper-pagination-${this.sectionId}`,
            clickable: true,
            type: 'custom',
            renderCustom: function (swiper, current, total) {
              let bulletsHtml = '';
              for (var i = 0; i < total; i++) {
                let el = swiper.slides[i];
                const bulletContent =
                  el.getAttribute('data-year') ||
                  el.getAttribute('data-subtitle') ||
                  el.querySelector('[data-small-image]')?.innerHTML;

                if (bulletContent) {
                  // Pagination classes
                  let activeClass = '';
                  if (i == current - 1) activeClass = ' swiper-pagination-bullet-active';

                  // Render bullet
                  bulletsHtml += `<div class="swiper-pagination-bullet${activeClass}">${bulletContent}</div>`;
                }
              }
              return bulletsHtml;
            },
          },
        };

        // Check if we have extra options on the HTML
        if (this.swiper.dataset.options) {
          const options = JSON.parse(this.swiper.dataset.options);

          if (options) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...options,
            };
          }
        }

        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

        // Common variables
        const defineCommonVars = (instance) => {
          // active
          this.activeSlide = instance.slides[instance.activeIndex];
          this.activeSlideInner = this.activeSlide.querySelector('.milestone__inner');
          this.activeSlideImage = this.activeSlide.querySelector('.milestone__image');
          this.activeSlideContent = this.activeSlide.querySelector('.milestone__content');
          this.activeSlideYear = parseInt(this.activeSlide.getAttribute('data-year') || 0);

          //prev
          this.prevSlide = instance.slides[instance.previousIndex];
          this.prevSlideInner = this.prevSlide.querySelector('.milestone__inner');
          this.prevSlideImage = this.prevSlide.querySelector('.milestone__image');
          this.prevSlideYear = parseInt(this.prevSlide.getAttribute('data-year') || 0);
        };

        // Common class logic
        const handleClassChange = (direction) => {
          if (self.activeSlideContent) self.activeSlideContent.style.opacity = 0;

          // Removal of the default classes
          self.activeSlideInner.classList.remove(
            'prev-forward',
            'prev-backward',
            'active-backward',
            'active-forward'
          );
          self.prevSlideInner.classList.remove(
            'prev-forward',
            'prev-backward',
            'active-backward',
            'active-forward'
          );

          // Addiction of the new class based on the direction
          self.activeSlideInner.classList.add(`active-${direction}`);
          self.prevSlideInner.classList.add(`prev-${direction}`);
        };

        // Common class logic
        const previousImageAsBackground = (instance) => {
          document.querySelector('.milestone__prev-image')?.remove();

          if (instance.activeIndex < instance.previousIndex) {
            self.prevSlide.style.opacity = 1;
            setTimeout(function () {
              self.prevSlide.style.opacity = 0;
            }, 500);

            const prevImageWrapper = document.createElement('div');
            prevImageWrapper.classList.add('milestone__prev-image');
            prevImageWrapper.innerHTML = this.prevSlideImage?.querySelector(
              '.milestone__image__wrapper'
            )?.innerHTML;
            self.activeSlideImage?.parentNode.prepend(prevImageWrapper);
          }
        };

        // Animation function
        const animateFloatingYear = (targetYear, currentYear) => {
          const counterElem = this.querySelector('rolling-year') || false;

          // Check if the rolling year is enabled
          if (!counterElem) return;

          // Get the elements for dozens and units digits
          const dozensElem = counterElem.querySelector('.dozens');
          const unitsElem = counterElem.querySelector('.units');

          // Renders the number helper NODE elements, activates the animation, removes the helpers again
          const renderNumberAnimation = (parentElement, prevNumber, nextNumber, delay = 0) => {
            setTimeout(function () {
              // Create prev NODE element
              const prevElem = document.createElement('div');
              prevElem.classList.add('prev-year');

              // Get current NODE element
              const currentElem = parentElement.querySelector('.current-year');

              // Render prev number
              prevElem.innerText = prevNumber;
              currentElem.after(prevElem);

              // Render the new number
              currentElem.innerText = nextNumber;

              // Activate number animation (CSS animation)
              parentElement.classList.add('rolling-animation');

              // Clean up the prev element and animation
              setTimeout(function () {
                prevElem.remove();
                parentElement.classList.remove('rolling-animation');
              }, 1250);
            }, delay);
          };

          // Dozens animation
          renderNumberAnimation(dozensElem, currentYear.toString()[2], targetYear.toString()[2]);

          // Units animation
          renderNumberAnimation(
            unitsElem,
            currentYear.toString()[3],
            targetYear.toString()[3],
            250
          );
        };

        // Animation logic. Do not show animation in editor
        if (!Shopify.designMode) {
          this.swiperInstance.on('activeIndexChange', function () {
            defineCommonVars(this);
            previousImageAsBackground(this);
            animateFloatingYear(self.activeSlideYear, self.prevSlideYear);
          });

          // Animation logic on click to the next slide || slideNextTransitionStart
          this.swiperInstance.on('slideNextTransitionStart', function () {
            handleClassChange('forward');
          });

          // Animation logic on click to the prev slide || slidePrevTransitionStart
          this.swiperInstance.on('slidePrevTransitionStart', function () {
            handleClassChange('backward');
          });
        }

        /**
         * Listen to extra events when in Shopify editor
         */
        if (Shopify.designMode) {
          // Update the swiper when the section event is triggered
          window.addEventListener('shopify:section:load', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
          });

          // When on block select go to the slide in front-end
          window.addEventListener('shopify:block:select', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });

          // When on block deselect go to the slide in front-end
          window.addEventListener('shopify:block:deselect', (event) => {
            this.swiperInstance.update();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });
        }
      }

      /**
       * Handles the theme editor block change/edit event
       * @param {Object} event
       */
      handleBlockSelect(event) {
        // Check if the slide index is set
        if (!('swiperSlideIndex' in event.target.dataset)) {
          return;
        }

        // Set the slide index based on loop settings or not
        let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
        if (this.swiperOptions.loop) {
          swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
        }

        // Slide to slide based on the data attribute from the target
        const sliderToUpdate = event.target.closest('[data-swiper]');
        sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
      }
    }

    window.TimelineSlider = TimelineSlider;

    customElements.define('timeline-slider', TimelineSlider);
  }
})();
